import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, type ModuleDetail, type ModuleDocument, type Quiz, type QuizResult } from "./api.ts";
import { Dialog } from "./Dialog.tsx";
import { DocumentDiffViewer } from "./DocumentDiffViewer.tsx";
import { formatDate } from "./date.ts";
import { Icon } from "./Icon.tsx";
import { messages } from "./i18n.ts";
import { PageHeader } from "./PageHeader.tsx";

// Lazily loaded, so a learner with Markdown content never downloads pdf.js.
const PdfDocument = lazy(() =>
  import("./PdfDocument.tsx").then((m) => ({ default: m.PdfDocument })),
);
// A plain link, from its own module with no pdf.js dependency: it is offered
// both beside an inline PDF viewer and beside an accessible Markdown primary
// that has a signed original one click away, and the second case must not
// pull pdf.js in just to render a link.
const OpenFullDocumentLink = lazy(() =>
  import("./OpenFullDocumentLink.tsx").then((m) => ({ default: m.OpenFullDocumentLink })),
);

function PdfLoading() {
  return (
    <p aria-live="polite" className="text-muted-foreground" role="status">
      {messages.module.pdfLoading}
    </p>
  );
}

function ProcedureHtml({ html, className }: { html: string; className?: string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const openerIndexRef = useRef(-1);
  const [openTableHtml, setOpenTableHtml] = useState<string | null>(null);

  const closeTable = useCallback(() => setOpenTableHtml(null), []);
  // React may replace the dangerouslySetInnerHTML subtree while the dialog is
  // open, so the button that opened it is looked up by position again rather
  // than held onto.
  const openerToRestore = useCallback(() => {
    const openers = Array.from(
      rootRef.current?.querySelectorAll<HTMLButtonElement>("[data-procedure-table-open]") ?? [],
    );
    return openers[openerIndexRef.current] ?? openerRef.current;
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || html.length === 0) return;

    const initialScrollers = Array.from(
      root.querySelectorAll<HTMLElement>(".procedure-table-scroll"),
    );
    const tables = initialScrollers.flatMap((scroller) => {
      const table = scroller.querySelector<HTMLTableElement>("table");
      return table ? [table] : [];
    });
    const updateOverflow = () => {
      // Query on every pass. React may replace the dangerouslySetInnerHTML
      // subtree while retaining this component and the same HTML value. A
      // captured element list would then update detached table frames and
      // leave the visible replacement without its deterministic marker.
      const currentOpeners = root.querySelectorAll<HTMLButtonElement>(
        "[data-procedure-table-open]",
      );
      for (const opener of currentOpeners) {
        // Avoid creating a child-list mutation on every observer callback.
        if (opener.textContent !== messages.module.openTable) {
          opener.textContent = messages.module.openTable;
        }
      }
      const currentScrollers = root.querySelectorAll<HTMLElement>(".procedure-table-scroll");
      for (const scroller of currentScrollers) {
        const frame = scroller.closest<HTMLElement>(".procedure-table-frame");
        if (!frame) continue;
        const table = scroller.querySelector<HTMLTableElement>("table");
        // WebKit can reflow a dense table just enough to make measured
        // overflow disappear after fonts settle. Keep the full-width option
        // stable for tables that are structurally dense as well as those that
        // currently overflow.
        const isWide =
          scroller.scrollWidth > scroller.clientWidth + 1 ||
          (table?.rows.item(0)?.cells.length ?? 0) >= 4;
        const hasContentToRight =
          isWide && scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
        frame.dataset.tableWide = String(isWide);
        frame.dataset.overflowRight = String(hasContentToRight);
      }
    };

    let active = true;
    updateOverflow();
    const animationFrame = window.requestAnimationFrame(updateOverflow);
    void document.fonts?.ready.then(() => {
      if (active) updateOverflow();
    });
    window.addEventListener("resize", updateOverflow);
    for (const scroller of initialScrollers) scroller.addEventListener("scroll", updateOverflow);

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateOverflow);
    observer?.observe(root);
    for (const scroller of initialScrollers) observer?.observe(scroller);
    for (const table of tables) observer?.observe(table);
    const mutationObserver = new MutationObserver(updateOverflow);
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      active = false;
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", updateOverflow);
      for (const scroller of initialScrollers) {
        scroller.removeEventListener("scroll", updateOverflow);
      }
    };
  }, [html]);

  const openTable = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLElement)) return;
    const opener = event.target.closest<HTMLButtonElement>("[data-procedure-table-open]");
    if (!opener || !event.currentTarget.contains(opener)) return;
    const table = opener
      .closest<HTMLElement>(".procedure-table-frame")
      ?.querySelector<HTMLTableElement>("table");
    if (!table) return;
    openerIndexRef.current = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>("[data-procedure-table-open]"),
    ).indexOf(opener);
    openerRef.current = opener;
    setOpenTableHtml(table.outerHTML);
  };

  return (
    <>
      {/* biome-ignore lint/a11y: click handling is delegated only to the native table-view buttons, which retain their keyboard behavior. */}
      <div
        ref={rootRef}
        className={`procedure ${className ?? ""}`}
        onClick={openTable}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: server-rendered from markdown by renderMarkdown() in api/src/http/routes/learner.ts, which escapes every `<` before parsing and allowlists link and image protocols to http/https/mailto, so neither a raw tag nor a javascript: URL survives.
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {openTableHtml ? (
        <Dialog
          bodyClassName="procedure procedure-table-full-view min-h-0 flex-1 overflow-auto p-3 sm:p-5"
          closeLabel={messages.module.closeTable}
          onClose={closeTable}
          restoreFocus={openerToRestore}
          title={messages.module.tableView}
        >
          <div
            // biome-ignore lint/security/noDangerouslySetInnerHtml: this is the exact table element copied from the already-sanitized procedure HTML above. No source text is reparsed or interpolated.
            dangerouslySetInnerHTML={{ __html: openTableHtml }}
          />
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * The line of chrome above a procedure that has changed since the learner last
 * passed it.
 *
 * One switch between the marked-up document and the clean one, the number of
 * edits, and a way to step between them. Nothing else moves, and the reading
 * area below is the same size in both views.
 */
function DocumentToolbar({
  document: revision,
  showingDiff,
  onToggle,
  onStep,
}: {
  document: ModuleDocument;
  showingDiff: boolean;
  onToggle: (showDiff: boolean) => void;
  onStep: (direction: -1 | 1) => void;
}) {
  const changeCount = revision.semanticDiff?.changeCount ?? 0;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-2.5 sm:px-5">
      <fieldset aria-label={messages.module.viewLabel} className="diff-segmented-control">
        <button aria-pressed={showingDiff} type="button" onClick={() => onToggle(true)}>
          {messages.module.whatChanged}
        </button>
        <button aria-pressed={!showingDiff} type="button" onClick={() => onToggle(false)}>
          {messages.module.fullProcedure}
        </button>
      </fieldset>

      <p className="text-[12px] text-muted-foreground">
        {messages.module.editsBetween(
          changeCount,
          revision.previousRevision ?? "",
          revision.revision,
        )}
      </p>

      {showingDiff && changeCount > 0 ? (
        <div className="flex items-center gap-1">
          <button
            aria-label={messages.module.previousEdit}
            className="grid size-[26px] place-items-center rounded-sm border border-border bg-card text-muted-foreground hover:text-primary"
            title={messages.module.previousEdit}
            type="button"
            onClick={() => onStep(-1)}
          >
            <Icon path={mdiChevronUp} size={16} />
          </button>
          <button
            aria-label={messages.module.nextEdit}
            className="grid size-[26px] place-items-center rounded-sm border border-border bg-card text-muted-foreground hover:text-primary"
            title={messages.module.nextEdit}
            type="button"
            onClick={() => onStep(1)}
          >
            <Icon path={mdiChevronDown} size={16} />
          </button>
        </div>
      ) : null}

      <div className="flex flex-1 items-center justify-end gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded-xs bg-status-complete-bg" />
          {messages.module.legendAdded}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded-xs bg-status-overdue-bg" />
          {messages.module.legendRemoved}
        </span>
      </div>
    </div>
  );
}

/**
 * The learner view. Document on the left, quiz on the right, both visible at
 * once: the quiz is open book on purpose, because looking the answer up in the
 * procedure is the learning rather than a way around it.
 *
 * There is no acknowledgement button. Passing the quiz is the evidence, and it
 * records the acknowledgement with the digest of exactly what was read, so a
 * separate "I have read this" click would only add a weaker claim.
 */
export function Module({
  obligationId,
  timezone,
  onBack,
}: {
  obligationId: string;
  timezone: string | null;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<ModuleDetail | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A revision the learner has already passed before opens on what changed,
  // because that is the only part they have not read. A first reading has
  // nothing to compare against and opens on the procedure itself.
  const [diffsHidden, setDiffsHidden] = useState<Record<string, boolean>>({});
  const bodyRefs = useRef(new Map<string, HTMLDivElement | null>());
  const editCursors = useRef(new Map<string, number>());
  const pageTopRef = useRef<HTMLSpanElement>(null);
  const quizRef = useRef<HTMLElement>(null);
  const load = useCallback(() => {
    api
      .module(obligationId)
      .then(setDetail)
      .catch(() => setError(messages.module.loadError));
  }, [obligationId]);

  useEffect(load, [load]);

  const beginQuiz = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setQuiz(await api.startQuiz(obligationId));
      setAnswers({});
    } catch {
      setError(messages.module.openError);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!quiz) return;
    setBusy(true);
    setError(null);
    try {
      const submitted = await api.submitQuiz(
        obligationId,
        quiz.attemptToken,
        quiz.questions.map((question) => ({
          questionId: question.questionId,
          optionId: answers[question.questionId] ?? "",
        })),
      );
      setResult(submitted);
      // A challenge grades exactly once. Retire its form and token after every
      // result so a failed learner starts a fresh attempt instead of sending a
      // consumed token that the server must reject.
      setQuiz(null);
      setAnswers({});
      load();
    } catch {
      // The request may have reached the server even when its response did not
      // reach the browser. A fresh challenge is safe in both cases.
      setQuiz(null);
      setAnswers({});
      setError(messages.module.submitError);
    } finally {
      setBusy(false);
    }
  };

  const stepEdit = (documentRevisionId: string, direction: -1 | 1) => {
    const body = bodyRefs.current.get(documentRevisionId);
    const edits = Array.from(body?.querySelectorAll<HTMLElement>("[data-edit-index]") ?? []);
    if (edits.length === 0) return;
    const current = editCursors.current.get(documentRevisionId);
    // Wraps deliberately. Somebody stepping through seven edits should not have
    // to notice which end they started from.
    const next =
      current === undefined
        ? direction === -1
          ? edits.length - 1
          : 0
        : (current + direction + edits.length) % edits.length;
    editCursors.current.set(documentRevisionId, next);
    edits[next]?.scrollIntoView({ block: "center", behavior: "smooth" });
  };

  if (!detail) {
    return (
      <main className="mx-auto min-w-0 max-w-3xl p-4 sm:p-8">
        <p className="text-muted-foreground">{error ?? messages.common.loading}</p>
      </main>
    );
  }

  const firstDocument = detail.documents[0];
  const completion = detail as ModuleDetail & { completedAt?: string | null; readOnly?: boolean };
  const completed = detail.state === "complete";
  const readOnly = detail.state === "complete" || completion.readOnly === true;
  const meta: { label: string; value: string; tone?: "overdue" }[] = [];
  if (firstDocument) {
    meta.push({
      label: messages.module.metaRevision,
      value: firstDocument.revision,
    });
  }
  // Only when the profile has loaded. A due date rendered against the wrong
  // calendar is worse than one not shown yet, and this screen deliberately
  // opens before the profile does.
  if (timezone && detail.dueAt) {
    meta.push({ label: messages.module.metaDue, value: formatDate(detail.dueAt, timezone) });
  }
  if (!readOnly) {
    meta.push({
      label: messages.module.metaQuiz,
      value: messages.module.quizSummary(
        detail.policy.questionsPerAttempt,
        Math.round(detail.policy.passMark * 100),
      ),
    });
  }

  const answeredAll = quiz
    ? quiz.questions.every((question) => Boolean(answers[question.questionId]))
    : false;

  return (
    <>
      <span aria-hidden="true" className="sr-only" ref={pageTopRef} />
      <PageHeader
        actions={
          completed ? (
            <span className="pill bg-status-complete-bg text-status-complete-fg">
              {messages.common.complete}
            </span>
          ) : null
        }
        back={{ label: messages.common.yourTraining, onClick: onBack }}
        meta={meta}
        subtitle={detail.description}
        title={firstDocument ? `${firstDocument.documentKey} ${detail.title}` : detail.title}
      />

      <main className="flex min-w-0 flex-col gap-4 p-3 sm:p-4 lg:p-6">
        {error ? <p className="text-destructive">{error}</p> : null}

        <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1.9fr)_minmax(22rem,1fr)] lg:gap-6">
          <section className="flex min-w-0 flex-col gap-4" aria-label={messages.module.documents}>
            {detail.documents.map((document) => {
              const comparable = Boolean(document.semanticDiff && document.previousRevision);
              const showingDiff = comparable && !diffsHidden[document.documentRevisionId];
              return (
                <article
                  key={document.documentRevisionId}
                  className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card"
                >
                  {document.title !== detail.title || document.acknowledged ? (
                    <div className="flex min-w-0 flex-col items-start gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-5">
                      {document.title !== detail.title ? (
                        <div className="flex min-w-0 flex-col">
                          <span className="break-words font-medium text-foreground">
                            {document.documentKey} {document.title}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {messages.module.revision(document.revision)}
                          </span>
                        </div>
                      ) : null}
                      {document.acknowledged ? (
                        <span className="pill bg-status-complete-bg text-status-complete-fg">
                          {messages.module.acknowledged}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {comparable ? (
                    <DocumentToolbar
                      document={document}
                      onStep={(direction) => stepEdit(document.documentRevisionId, direction)}
                      onToggle={(showDiff) =>
                        setDiffsHidden((current) => ({
                          ...current,
                          [document.documentRevisionId]: !showDiff,
                        }))
                      }
                      showingDiff={showingDiff}
                    />
                  ) : null}

                  {document.changeSummaryHtml ? (
                    <div className="min-w-0 border-b border-border bg-status-in-progress-bg px-4 py-3 sm:px-5">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-status-in-progress-fg">
                        {messages.module.revisionReason}
                      </p>
                      <ProcedureHtml html={document.changeSummaryHtml} className="text-sm" />
                    </div>
                  ) : null}

                  {document.contentType === "pdf" && document.pdfUrl ? (
                    // Always offered, not only on failure: a screen reader user
                    // or somebody who wants the browser's own viewer should
                    // not have to wait for the canvas rendering to fail first.
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2 text-xs text-muted-foreground sm:px-5">
                      <span>{messages.module.pdfNotice}</span>
                      <Suspense fallback={null}>
                        <OpenFullDocumentLink url={document.pdfUrl} />
                      </Suspense>
                    </div>
                  ) : null}

                  {document.renditionKind === "signed_original" && document.pdfUrl ? (
                    // The accessible Markdown remains the primary reading
                    // experience; the exact signed document it was produced
                    // from stays one click away, never inline, so a
                    // Markdown-only learner never pays for pdf.js.
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2 text-xs text-muted-foreground sm:px-5">
                      <span>{messages.module.signedOriginalNotice}</span>
                      <Suspense fallback={null}>
                        <OpenFullDocumentLink
                          label={messages.module.viewSignedOriginal}
                          url={document.pdfUrl}
                        />
                      </Suspense>
                    </div>
                  ) : null}

                  {/*
                  Not `.procedure` itself: ProcedureHtml owns that class, and the
                  mobile layout spec measures the first `.procedure` on the page
                  to prove the document fits a phone.
                */}
                  <div
                    className="min-w-0 px-4 py-4 text-sm leading-relaxed text-foreground sm:px-5 lg:max-h-[calc(100dvh-13rem)] lg:overflow-y-auto"
                    ref={(element) => {
                      bodyRefs.current.set(document.documentRevisionId, element);
                    }}
                  >
                    {document.contentType === "pdf" && document.pdfUrl ? (
                      <Suspense fallback={<PdfLoading />}>
                        <PdfDocument
                          label={messages.module.pdfRegion(document.documentKey, document.revision)}
                          url={document.pdfUrl}
                        />
                      </Suspense>
                    ) : showingDiff && document.semanticDiff ? (
                      <DocumentDiffViewer diff={document.semanticDiff} />
                    ) : (
                      <ProcedureHtml html={document.html ?? ""} />
                    )}

                    {/*
                  Document control, at the end rather than interleaved. The
                  signature block and page furniture are not the procedure, but a
                  reader should be able to see which revision they were shown and
                  that it is pinned, which is what the training record will later
                  claim on their behalf.
                */}
                    <dl className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground">
                      <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-1">
                        <dt className="font-medium text-foreground">{messages.module.document}</dt>
                        <dd>
                          {messages.module.documentRevision(
                            document.documentKey,
                            document.revision,
                          )}
                        </dd>
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-1">
                        <dt className="font-medium text-foreground">{messages.module.digest}</dt>
                        <dd className="break-all font-mono">{document.blobSha256.slice(0, 16)}</dd>
                      </div>
                    </dl>

                    <nav
                      aria-label={messages.module.mobileNavigation}
                      className="mt-5 grid grid-cols-2 gap-2 lg:hidden"
                    >
                      <button
                        className="rounded-md border border-border bg-card px-3 py-2 font-medium text-foreground"
                        type="button"
                        onClick={() =>
                          pageTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }
                      >
                        {messages.module.backToTop}
                      </button>
                      <button
                        className="rounded-md bg-primary px-3 py-2 font-medium text-primary-foreground"
                        type="button"
                        onClick={() =>
                          quizRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }
                      >
                        {completed
                          ? messages.module.viewCompletion
                          : readOnly
                            ? messages.module.readOnly
                            : messages.module.goToQuestions}
                      </button>
                    </nav>
                  </div>
                </article>
              );
            })}
          </section>

          <section
            aria-label={messages.module.quiz}
            className="flex min-w-0 flex-col gap-4"
            ref={quizRef}
          >
            <div className="flex min-w-0 flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-5 lg:sticky lg:top-6">
              <div className="flex flex-col gap-1">
                <h2 className="font-semibold text-foreground">{messages.module.comprehension}</h2>
                <p className="text-sm text-muted-foreground">
                  {completed
                    ? completion.completedAt && timezone
                      ? messages.home.completedOn(formatDate(completion.completedAt, timezone))
                      : messages.home.completedWithApplicableQuiz
                    : detail.state === "superseded"
                      ? messages.module.superseded
                      : detail.state === "cancelled"
                        ? messages.module.cancelled
                        : readOnly
                          ? messages.module.readOnly
                          : messages.module.quizPolicy(
                              detail.policy.questionsPerAttempt,
                              Math.round(detail.policy.passMark * 100),
                            )}
                </p>
              </div>

              {readOnly || result?.passed ? null : !quiz ? (
                <button
                  className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground disabled:opacity-50"
                  type="button"
                  disabled={busy}
                  onClick={beginQuiz}
                >
                  {result || detail.attempts.length > 0
                    ? messages.module.tryAgain
                    : messages.module.start}
                </button>
              ) : (
                <form className="flex min-w-0 flex-col gap-5" onSubmit={submit}>
                  {quiz.questions.map((question, index) => (
                    <fieldset key={question.questionId} className="flex min-w-0 flex-col gap-2">
                      <legend className="mb-1 max-w-full break-words text-sm font-medium text-foreground">
                        {index + 1}. {question.prompt}
                      </legend>
                      {question.options.map((option) => (
                        <label
                          key={option.optionId}
                          className="flex min-w-0 cursor-pointer items-start gap-2 rounded-md border border-border px-3 py-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-accent"
                        >
                          <input
                            className="mt-0.5"
                            type="radio"
                            name={question.questionId}
                            value={option.optionId}
                            checked={answers[question.questionId] === option.optionId}
                            onChange={() =>
                              setAnswers((current) => ({
                                ...current,
                                [question.questionId]: option.optionId,
                              }))
                            }
                          />
                          <span className="min-w-0 break-words text-foreground">{option.text}</span>
                        </label>
                      ))}
                    </fieldset>
                  ))}
                  <button
                    className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground disabled:opacity-50"
                    type="submit"
                    disabled={busy || !answeredAll}
                  >
                    {messages.module.submit}
                  </button>
                </form>
              )}

              {result ? (
                <div
                  className={`rounded-md px-3 py-3 text-sm ${result.passed ? "bg-status-complete-bg text-status-complete-fg" : "bg-status-overdue-bg text-status-overdue-fg"}`}
                >
                  <p className="font-medium">
                    {result.passed ? messages.module.passed : messages.module.notPassed} ·{" "}
                    {Math.round(result.score * 100)}%
                  </p>
                  <p className="mt-1">
                    {result.passed
                      ? messages.module.recorded
                      : messages.module.retry(Math.round(detail.policy.passMark * 100))}
                  </p>
                </div>
              ) : null}

              {detail.attempts.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {messages.module.attempts(detail.attempts.length)}
                </p>
              ) : null}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
